import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import AutoDeleteOutlinedIcon from "@mui/icons-material/AutoDeleteOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  bob,
  cloudLift,
  floatOrbit,
  motionSafe,
  rise,
  softPulse,
  staggerIn,
  studioDialogBackdropSx,
  studioDialogPaperSx,
} from "./studioDialogMotion";
import { AI_DESIGN_HISTORY_RETENTION_DAYS } from "./aiDesignsStudioState";

const ACCENT = "#0f766e";

function RetentionHeroStage({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 112,
        mt: 1.35,
        mb: 0.25,
        borderRadius: 2.5,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.07),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.35)}`,
        animation: `${rise} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 90,
          height: 90,
          borderRadius: "50%",
          left: -18,
          top: -28,
          bgcolor: alpha(accent, 0.14),
          filter: "blur(2px)",
          animation: `${softPulse} 3.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 64,
          height: 64,
          borderRadius: "50%",
          right: -8,
          bottom: -18,
          bgcolor: alpha(accent, 0.1),
          animation: `${floatOrbit} 5s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: "18%",
          top: "28%",
          width: 52,
          height: 52,
          borderRadius: 2,
          bgcolor: alpha(accent, 0.18),
          border: `1px solid ${alpha(accent, 0.35)}`,
          display: "grid",
          placeItems: "center",
          animation: `${bob} 2.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <ScheduleOutlinedIcon sx={{ fontSize: 26, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: "22%",
          top: "22%",
          width: 58,
          height: 58,
          borderRadius: "50%",
          bgcolor: alpha(accent, 0.2),
          border: `1px solid ${alpha(accent, 0.4)}`,
          display: "grid",
          placeItems: "center",
          animation: `${cloudLift} 2.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <AutoDeleteOutlinedIcon sx={{ fontSize: 28, color: accent }} />
      </Box>
    </Box>
  );
}

/**
 * Aviso tras la limpieza automática del historial (>15 días).
 */
export default function HistoryRetentionDialog({
  open,
  purgedCount = 0,
  retentionDays = AI_DESIGN_HISTORY_RETENTION_DAYS,
  onClose,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = ACCENT;
  const count = Math.max(0, Number(purgedCount) || 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      keepMounted={false}
      transitionDuration={{ enter: 280, exit: 160 }}
      slotProps={{ backdrop: { sx: studioDialogBackdropSx } }}
      BackdropProps={{ sx: studioDialogBackdropSx }}
      PaperProps={{
        sx: {
          ...studioDialogPaperSx,
          borderRadius: 3.5,
          borderColor: alpha(accent, 0.22),
          boxShadow: `0 28px 64px ${alpha(accent, 0.22)}, 0 8px 24px rgba(15,23,42,0.18)`,
          background: isDark
            ? `linear-gradient(165deg, ${alpha(accent, 0.12)} 0%, ${theme.palette.background.paper} 42%)`
            : `linear-gradient(165deg, ${alpha(accent, 0.09)} 0%, #fff 48%)`,
          animation: open
            ? `${rise} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`
            : undefined,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          px: 2.75,
          pt: 2.25,
          pb: 1.5,
          overflow: "hidden",
          borderBottom: "1px solid",
          borderColor: alpha(accent, 0.14),
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse at 12% 0%, ${alpha(
              accent,
              0.22,
            )} 0%, transparent 55%)`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            animation: `${staggerIn} 360ms cubic-bezier(0.22, 1, 0.36, 1) both`,
            ...motionSafe,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(accent, 0.16),
              color: accent,
              border: `1px solid ${alpha(accent, 0.28)}`,
              animation: `${cloudLift} 2.8s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            <AutoDeleteOutlinedIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 900, lineHeight: 1.2, letterSpacing: -0.2 }}
            >
              {t("presentacionesSearch.newDesign.retentionTitle", {
                defaultValue: "Historial limpio automáticamente",
              })}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.55, lineHeight: 1.45 }}
            >
              {t("presentacionesSearch.newDesign.retentionLead", {
                defaultValue:
                  "Para no llenar el disco, las versiones de AI Designs se eliminan solas a los {{days}} días — aunque no abras el studio.",
                days: retentionDays,
              })}
            </Typography>
          </Box>
        </Box>
        <RetentionHeroStage accent={accent} />
      </Box>

      <DialogContent sx={{ pt: 2.25, px: 2.75 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            animation: `${staggerIn} 320ms ease both`,
            ...motionSafe,
          }}
        >
          {count > 0
            ? t("presentacionesSearch.newDesign.retentionBodyCount", {
                defaultValue:
                  "Se liberaron {{count}} versión(es) de este AI Design.",
                count,
              })
            : t("presentacionesSearch.newDesign.retentionBody", {
                defaultValue:
                  "Las fotos antiguas del historial se han liberado del servidor.",
              })}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, lineHeight: 1.5 }}
        >
          {t("presentacionesSearch.newDesign.retentionHint", {
            defaultValue:
              "Lo que ya volcaste a ficha técnica o moodboard no se toca. Solo el historial de la derecha.",
          })}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 2.75, pb: 2.25, pt: 0.5 }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 800,
            bgcolor: accent,
            "&:hover": { bgcolor: "#0d9488" },
          }}
        >
          {t("presentacionesSearch.newDesign.retentionOk", {
            defaultValue: "Entendido",
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
